import { APIError } from './APIError';
import { HttpStatusCode } from './HttpStatusCode';
import type { Music24QuotaKind } from '../license/music24Plans';

/** Team refusals (plan 3). They travel in the same carrier as the plan limits. */
export type Music24TeamErrorCode =
  | 'M24_TEAM_OWNER_ONLY'
  | 'M24_TEAM_INVITE_INVALID'
  | 'M24_TEAM_EMAIL_MISMATCH'
  | 'M24_TEAM_ALREADY_MEMBER'
  | 'M24_TEAM_OWNER_CANNOT_LEAVE'
  | 'M24_TEAM_HOME_CANNOT_LEAVE'
  | 'M24_TEAM_NOT_A_MEMBER';

/**
 * Every Music24 code the apps map to a screen: plan limits, team refusals (plan 3) and the
 * disposable-email refusal at signup (plan 1).
 */
export type Music24LimitCode =
  | 'M24_LIMIT_PAGES'
  | 'M24_LIMIT_ARTISTS'
  | 'M24_LIMIT_SOCIAL'
  | 'M24_LIMIT_QUOTA'
  | 'M24_LIMIT_RELEASES'
  | 'M24_LIMIT_SEATS'
  | 'M24_LIMIT_FEATURE'
  | 'M24_DISPOSABLE_EMAIL'
  | 'M24_DEMO_READ_ONLY'
  | 'M24_LIMIT_ARTIST_READ_ONLY'
  | Music24TeamErrorCode;

/** Boolean capabilities that a route can refuse with M24_LIMIT_FEATURE. */
export type Music24GatedFeature =
  | 'canExportFans'
  | 'canUsePresave'
  | 'canUseCustomDomain'
  | 'canExport'
  | 'canFilterGeographically'
  | 'canFilterByMusicService'
  | 'canUseAiReels'
  | 'canRenderFullSongVideos'
  | 'canMakeAiFootage'
  | 'canPushOldSongs'
  /** Connect, publish and the daily read of X (`canUseX`: Pro, Plug, Manager, Label). */
  | 'canUseX'
  /**
   * Auto-posting to Facebook, Instagram and Threads while these logins go through Zernio
   * (`SOCIAL_ZERNIO_PLATFORMS`, a temporary middleman that bills each account): paid plans only.
   */
  | 'canAutoPostToMeta'
  /** More than 1 post a day in an Auto campaign (`reelsPlanMaxPostsPerDay`: 3 on paid plans, 1 on Free and the trial). */
  | 'reelsPlanPostsPerDay'
  | 'canCustomizeKitLook'
  | 'canDownloadKitZip'
  | 'canSeeEarnings'
  | 'canSplitStreaming'
  /** The AI pictures of the cover editor (fym-shared `canUseCoverAi`; not a capabilities field). */
  | 'canUseCoverAi'
  /** The ProRes download of the motion artwork (fym-shared `canDownloadProRes`: Pro, Plug, Manager, Label). */
  | 'canDownloadProRes'
  /** Outreach as a whole (a caller with no licence, or a plan with no Outreach row). */
  | 'outreach'
  /** Autopilot mode of an Outreach campaign (`canUseOutreachAutopilot`: Plug and Label). */
  | 'outreachAutopilot'
  /** The CSV export of the Outreach contacts (`canExportOutreachContacts`: Pro and up). */
  | 'outreachExport'
  /** One more own Outreach mailbox (`outreachMailboxes`). */
  | 'outreachMailboxes';

export interface Music24LimitPayload {
  code: Music24LimitCode;
  /** The plan limit. Null means unlimited; it never appears on a refusal. */
  limit?: number | null;
  used?: number;
  kind?: Music24QuotaKind;
  feature?: Music24GatedFeature;
  /** ISO time of the next monthly reset. Only for M24_LIMIT_QUOTA. */
  resetsAt?: string;
  /**
   * Credits left in the workspace's packs. Only for M24_LIMIT_QUOTA of kind `videoCredit` (video
   * credit packs) and `outreachReveal` (the pitch credits of the Outreach pitch packs).
   */
  packBalance?: number;
  /**
   * Only for M24_LIMIT_ARTISTS on a plan that sells extra artists (Plus, Pro, Max, Ultra) below its
   * cap: the workspace can buy one more artist at this monthly price (the plan price minus 20 %).
   */
  offer?: {
    /** The monthly price of one extra artist in US cents (integer): Plus 1520, Pro 3120, Max 5520, Ultra 19920. */
    extraArtistMonthlyCents: number;
    /**
     * @deprecated The same price in whole US dollars (`Math.round(extraArtistMonthlyCents / 100)`,
     * for example 31). Shipped native apps decode it as an integer, so it is never fractional.
     * Kept for clients built before 2026-10-23; read `extraArtistMonthlyCents`.
     */
    extraArtistPriceUsd: number;
  };
}

const MESSAGES: Record<Music24LimitCode, string> = {
  M24_LIMIT_PAGES: 'Your plan has no free page left. Unpublish a page or upgrade your plan.',
  M24_LIMIT_ARTISTS:
    'Your plan has no free artist left. Add an extra artist in Settings → Subscription, or upgrade your plan.',
  M24_LIMIT_SOCIAL: 'Your plan has no free social account left. Disconnect an account or upgrade your plan.',
  M24_LIMIT_QUOTA: 'You used all of this month on your plan. Wait for the reset or upgrade your plan.',
  M24_LIMIT_RELEASES: 'Your plan has no free release slot left. Finish a release or upgrade your plan.',
  M24_LIMIT_SEATS: 'Your plan has no free seat left. Upgrade your plan to invite a person.',
  M24_LIMIT_FEATURE: 'Your plan does not include this feature. Upgrade your plan to use it.',
  M24_DISPOSABLE_EMAIL: 'We do not accept disposable email addresses. Use a permanent email address.',
  M24_DEMO_READ_ONLY:
    'LaziiBee is an example artist and you cannot change it. Switch to your own artist to make changes.',
  M24_LIMIT_ARTIST_READ_ONLY:
    'Your plan has fewer artists than this workspace. You can see this artist but not change it. Upgrade your plan to change it.',
  M24_TEAM_OWNER_ONLY: 'Only the workspace owner can do this.',
  M24_TEAM_INVITE_INVALID: 'This invite is not valid. It expired, was revoked or was used.',
  M24_TEAM_EMAIL_MISMATCH: 'This invite is for a different email address.',
  M24_TEAM_ALREADY_MEMBER: 'This person is already in the workspace.',
  M24_TEAM_OWNER_CANNOT_LEAVE: 'The owner cannot leave the workspace.',
  M24_TEAM_HOME_CANNOT_LEAVE: 'This is your own workspace. You cannot leave it.',
  M24_TEAM_NOT_A_MEMBER: 'This person is not a member of the workspace.',
};

const CODES: ReadonlySet<string> = new Set<string>(Object.keys(MESSAGES));

export function music24LimitMessage(payload: Music24LimitPayload): string {
  return MESSAGES[payload.code];
}

export class Music24LimitError extends APIError {
  public readonly payload: Music24LimitPayload;

  /** 402 for a plan limit. Plan 3 passes 403 for a REST team refusal (`M24_TEAM_OWNER_ONLY`). */
  constructor(
    payload: Music24LimitPayload,
    message?: string,
    httpStatusCode: HttpStatusCode = HttpStatusCode.PaymentRequired
  ) {
    super({
      message: message ?? music24LimitMessage(payload),
      httpStatusCode,
      isOperational: true,
    });
    this.name = 'Music24LimitError';
    this.payload = payload;
  }
}

/** An Error whose only enumerable property is `m24Limit`. tRPC error formatters copy it into `data`. */
export function music24LimitCause(payload: Music24LimitPayload): Error & { m24Limit: Music24LimitPayload } {
  return Object.assign(new Error(music24LimitMessage(payload)), { m24Limit: payload });
}

export function readMusic24Limit(value: unknown): Music24LimitPayload | null {
  if (value === null || typeof value !== 'object') {
    return null;
  }
  const candidate = (value as { m24Limit?: unknown }).m24Limit;
  if (candidate === null || typeof candidate !== 'object') {
    return null;
  }
  const { code } = candidate as { code?: unknown };
  return typeof code === 'string' && CODES.has(code) ? (candidate as Music24LimitPayload) : null;
}
